import { For, Show, createMemo, createSignal } from "solid-js"
import type { MarketplaceSkill } from "./skill-market-types"

/**
 * 技能图标
 *
 * 三级降级，全部在前端完成，不依赖后端改动：
 *   1. skill.icon_url 有值 → 直接显示（后端以后填充即自动生效）
 *   2. 有 category → 按分类取单色 glyph
 *   3. 都没有 → display_name 首字做 monogram
 *
 * 底色由 name hash 稳定决定：同一技能每次渲染颜色一致，不同技能之间有区分度。
 */

// 浅色底 + 同色系深色前景，与 app 现有浅色主题一致
const TINTS = [
  { bg: "#E8F1FE", fg: "#2563C9" },
  { bg: "#E1F5EE", fg: "#0F6E56" },
  { bg: "#EEEDFE", fg: "#534AB7" },
  { bg: "#FAEEDA", fg: "#96650F" },
  { bg: "#FBEAF0", fg: "#993556" },
  { bg: "#EAF3DE", fg: "#4E7A16" },
  { bg: "#FAECE7", fg: "#B24A26" },
]

// 24x24 viewBox，统一 stroke 风格
const GLYPHS: Record<string, string[]> = {
  all: ["M4 4h6v6H4z", "M14 4h6v6h-6z", "M4 14h6v6H4z", "M14 14h6v6h-6z"],
  "security-audit": ["M12 3l7 2.6v5.6c0 4.2-3 7.4-7 9.2-4-1.8-7-5-7-9.2V5.6L12 3z", "M9 12l2 2 4-4"],
  "vulnerability-scan": [
    "M12 5a7 7 0 100 14 7 7 0 000-14z",
    "M12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z",
    "M12 2v2",
    "M12 20v2",
    "M2 12h2",
    "M20 12h2",
  ],
  compliance: ["M7 3h7l4 4v14H7z", "M14 3v4h4", "M9.5 14.5l2 2 3.5-3.5"],
  "incident-response": ["M12 3.5l8.5 15h-17z", "M12 9v4.5", "M12 16.2v.1"],
  forensics: ["M10.5 4a6.5 6.5 0 100 13 6.5 6.5 0 000-13z", "M15.4 15.4L21 21"],
  other: ["M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"],
  default: ["M12 3l2.4 6.1L21 11l-6.6 1.9L12 19l-2.4-6.1L3 11l6.6-1.9L12 3z"],
}

function hashString(input: string) {
  let hash = 5381
  for (let i = 0; i < input.length; i++) hash = (hash * 33 + input.charCodeAt(i)) >>> 0
  return hash
}

/** 分类单色 glyph（左侧导航栏与卡片共用） */
export function SkillGlyph(props: { code?: string; size?: number; color?: string }) {
  const paths = () => GLYPHS[props.code ?? ""] ?? GLYPHS.default
  const size = () => props.size ?? 18
  return (
    <svg
      width={size()}
      height={size()}
      viewBox="0 0 24 24"
      fill="none"
      stroke={props.color ?? "currentColor"}
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <For each={paths()}>{(d) => <path d={d} />}</For>
    </svg>
  )
}

export function SkillMarketIcon(props: { skill: MarketplaceSkill; size?: number }) {
  const size = () => props.size ?? 36
  const tint = createMemo(() => TINTS[hashString(props.skill.name || props.skill.id || "") % TINTS.length])
  const [failedId, setFailedId] = createSignal<string | null>(null)

  // 只屏蔽「当前这个技能」加载失败的图，切换技能后会重新尝试
  const iconSrc = () =>
    props.skill.icon_url && failedId() !== props.skill.id ? props.skill.icon_url : undefined

  const monogram = () => {
    const label = (props.skill.display_name ?? props.skill.name ?? "").trim()
    const first = Array.from(label)[0]
    if (!first) return "?"
    return /[a-z]/.test(first) ? first.toUpperCase() : first
  }

  return (
    <div
      class="skill-market-icon"
      style={{
        width: `${size()}px`,
        height: `${size()}px`,
        "border-radius": `${Math.round(size() * 0.28)}px`,
        background: tint().bg,
      }}
    >
      <Show
        when={iconSrc()}
        fallback={
          <Show
            when={props.skill.category}
            fallback={
              <span
                class="skill-market-icon-mono"
                style={{ "font-size": `${Math.round(size() * 0.42)}px`, color: tint().fg }}
              >
                {monogram()}
              </span>
            }
          >
            <SkillGlyph
              code={props.skill.category}
              size={Math.round(size() * 0.52)}
              color={tint().fg}
            />
          </Show>
        }
      >
        {(src) => (
          <img
            class="skill-market-icon-img"
            src={src()}
            alt=""
            onError={() => setFailedId(props.skill.id)}
          />
        )}
      </Show>
    </div>
  )
}
